import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, ScrollView, TextInput } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';
import { PRIORITY_OPTIONS } from '../../config/negotiationConfig';
import logger from '../../utils/logger';

// --- Styled Components ---

const Overlay = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
`;

const ModalCard = styled.View`
  width: 90%;
  max-width: 420px;
  max-height: 85%;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 16px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  overflow: hidden;
`;

const Header = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom-width: 1px;
  border-bottom-color: ${REDESIGN_COLORS.borderSubtle};
`;

const HeaderTitle = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 18px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const CloseButton = styled(Pressable)`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.1);
  justify-content: center;
  align-items: center;
`;

const CloseText = styled.Text`
  font-size: 16px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const Content = styled.View`
  padding: 20px;
`;

const SectionTitle = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 8px;
`;

const SectionDescription = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 12px;
`;

const BudgetInputWrapper = styled.View`
  flex-direction: row;
  align-items: center;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 4px;
`;

const CurrencyPrefix = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 16px;
  color: ${REDESIGN_COLORS.textSecondary};
  padding-left: 14px;
`;

const PriorityItem = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${({ $isSelected }) =>
    $isSelected ? REDESIGN_COLORS.ctaBlue : REDESIGN_COLORS.borderSubtle};
  background-color: ${({ $isSelected }) =>
    $isSelected ? 'rgba(5, 86, 247, 0.12)' : 'transparent'};
  margin-bottom: 6px;
`;

const PriorityRank = styled.View`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: ${({ $isSelected }) =>
    $isSelected ? REDESIGN_COLORS.ctaBlue : 'rgba(255, 255, 255, 0.1)'};
  justify-content: center;
  align-items: center;
  margin-right: 10px;
`;

const PriorityRankText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const PriorityLabel = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
  flex: 1;
`;

const Footer = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top-width: 1px;
  border-top-color: ${REDESIGN_COLORS.borderSubtle};
`;

const CancelBtn = styled(Pressable)`
  padding: 12px 20px;
  border-radius: 8px;
`;

const CancelBtnText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const SaveBtn = styled(Pressable)`
  background-color: ${REDESIGN_COLORS.ctaBlue};
  padding: 12px 24px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const SaveBtnText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const WarningText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  color: ${REDESIGN_COLORS.scoreMedium};
  margin-top: 4px;
  margin-bottom: 12px;
`;

const ErrorText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  color: #ef4444;
  margin-bottom: 8px;
`;

/**
 * PreferenceEditor - Modal for mid-negotiation budget/priority updates.
 *
 * Reuses the budget input and priority ranker patterns from the setup screen.
 * On save: calls onSave with the updated preferences, which triggers
 * an update to negotiations.setup_config and a new draft generation.
 * PostHog: negotiation_preference_updated event.
 *
 * @param {Object} props
 * @param {Object} props.negotiation - The negotiation object
 * @param {boolean} props.visible - Whether the modal is shown
 * @param {Function} props.onClose - Called to dismiss
 * @param {Function} props.onSave - Called with { budget, priorities }
 */
const PreferenceEditor = ({ negotiation, visible, onClose, onSave }) => {
  const [budget, setBudget] = useState('');
  const [priorities, setPriorities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [budgetWarning, setBudgetWarning] = useState(null);

  const askingPrice = negotiation?.listing_snapshot?.price || negotiation?.asking_price_at_start;

  // Reset state when modal opens.
  //
  // Source of truth at setup time:
  //   budget     → negotiations.buyer_reservation_price (row column)
  //   priorities → negotiations.setup_config.priorities_ranked (jsonb key)
  // The legacy reads below (setup_config.budget, setup_config.priorities) are
  // kept as soft fallbacks in case any older row was written that way.
  useEffect(() => {
    if (visible && negotiation) {
      const setupConfig = negotiation.setup_config || {};
      const initialBudget =
        negotiation.buyer_reservation_price ?? setupConfig.budget ?? '';
      setBudget(initialBudget ? String(initialBudget) : '');
      const initialPriorities =
        setupConfig.priorities_ranked ?? setupConfig.priorities ?? [];
      setPriorities(Array.isArray(initialPriorities) ? initialPriorities : []);
      setError(null);
      setBudgetWarning(null);
    }
  }, [visible, negotiation]);

  const handleBudgetChange = useCallback(
    (value) => {
      setBudget(value);
      const numericValue = Number(value.replace(/[^0-9.]/g, ''));
      if (numericValue && askingPrice) {
        if (numericValue >= askingPrice * 0.95) {
          setBudgetWarning('Very close to asking price. Less room to negotiate.');
        } else if (numericValue < askingPrice * 0.5) {
          setBudgetWarning('More than 50% below asking price. Dealer may not engage.');
        } else {
          setBudgetWarning(null);
        }
      } else {
        setBudgetWarning(null);
      }
    },
    [askingPrice]
  );

  const handlePriorityToggle = useCallback((priority) => {
    setPriorities((prev) => {
      if (prev.includes(priority)) {
        return prev.filter((p) => p !== priority);
      }
      return [...prev, priority];
    });
  }, []);

  const handleSave = useCallback(async () => {
    const numericBudget = Number(budget.replace(/[^0-9.]/g, ''));
    if (!numericBudget || numericBudget <= 0) {
      setError('Please enter a valid budget.');
      return;
    }
    if (numericBudget > 999999) {
      setError('Budget cannot exceed 999,999.');
      return;
    }
    if (askingPrice && numericBudget >= askingPrice) {
      setError('Budget must be below the asking price.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (onSave) {
        await onSave({
          budget: numericBudget,
          priorities,
        });
      }
      onClose();
    } catch (err) {
      logger.error('[PreferenceEditor] Save failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [budget, priorities, askingPrice, onSave, onClose]);

  return (
    <Modal animationType="fade" transparent visible={visible} onRequestClose={onClose}>
      <Overlay>
        <ModalCard>
          <Header>
            <HeaderTitle>Update preferences</HeaderTitle>
            <CloseButton onPress={onClose}>
              <CloseText>{'\u2715'}</CloseText>
            </CloseButton>
          </Header>

          <ScrollView>
            <Content>
              {/* Budget section */}
              <SectionTitle>Maximum budget</SectionTitle>
              <SectionDescription>
                The most you are willing to pay for this car.
              </SectionDescription>
              <BudgetInputWrapper>
                <CurrencyPrefix>{'\u00A3'}</CurrencyPrefix>
                <TextInput
                  style={{
                    flex: 1,
                    fontFamily: 'Satoshi-Medium',
                    fontSize: 16,
                    color: REDESIGN_COLORS.textPrimary,
                    paddingVertical: 12,
                    paddingHorizontal: 12,
                    outlineStyle: 'none',
                  }}
                  value={budget}
                  onChangeText={handleBudgetChange}
                  placeholder="e.g. 15,000"
                  placeholderTextColor={REDESIGN_COLORS.textSecondary}
                  keyboardType="numeric"
                  editable={!saving}
                />
              </BudgetInputWrapper>
              {budgetWarning && <WarningText>{budgetWarning}</WarningText>}

              {/* Priority section */}
              <SectionTitle style={{ marginTop: budgetWarning ? 8 : 24 }}>Priorities</SectionTitle>
              <SectionDescription>
                Select what matters most. This helps shape the negotiation strategy.
              </SectionDescription>
              {PRIORITY_OPTIONS.map((option) => {
                const rank = priorities.indexOf(option.value);
                const isSelected = rank >= 0;
                return (
                  <PriorityItem
                    key={option.value}
                    $isSelected={isSelected}
                    onPress={() => handlePriorityToggle(option.value)}
                    disabled={saving}
                  >
                    <PriorityRank $isSelected={isSelected}>
                      <PriorityRankText>{isSelected ? rank + 1 : ''}</PriorityRankText>
                    </PriorityRank>
                    <PriorityLabel>{option.label}</PriorityLabel>
                  </PriorityItem>
                );
              })}

              {error && <ErrorText>{error}</ErrorText>}
            </Content>
          </ScrollView>

          <Footer>
            <CancelBtn onPress={onClose} disabled={saving}>
              <CancelBtnText>Cancel</CancelBtnText>
            </CancelBtn>
            <SaveBtn onPress={handleSave} disabled={saving}>
              {saving ? (
                <ActivityIndicator size="small" color={REDESIGN_COLORS.textPrimary} />
              ) : (
                <SaveBtnText>Save changes</SaveBtnText>
              )}
            </SaveBtn>
          </Footer>
        </ModalCard>
      </Overlay>
    </Modal>
  );
};

export default PreferenceEditor;
